import React from "react";
import { Link } from "react-router-dom";
import {
  FaCalendarAlt,
  FaUser,
  FaClock,
  FaChevronLeft,
  FaShare,
  FaLinkedin,
  FaTwitter,
  FaFacebook,
  FaFileAlt,
  FaCalculator,
  FaBuilding,
} from "react-icons/fa";
import Header from "../components/Header";
import Footer from "../components/Footer";

const DetailBlog7 = () => {
  const blogPost = {
    id: 4,
    title: "Société civile au Bénin : quel régime fiscal choisir ?",
    excerpt:
      "La Société Civile, et notamment la Société Civile Immobilière (SCI), est une forme juridique très prisée au Bénin pour gérer un patrimoine. Découvrez comment maîtriser sa fiscalité et choisir le régime le plus adapté à votre situation.",
    content: `
      <div class="intro-section">
        <p class="lead-text">La Société Civile, et notamment la Société Civile Immobilière (SCI), est une forme juridique très prisée au Bénin pour gérer un patrimoine, qu'il soit immobilier ou professionnel. Si sa souplesse de gestion est un atout majeur, son régime fiscal est souvent mal compris et mérite une attention particulière.</p>
        
        <div class="question-box">
          <h4>Les questions essentielles :</h4>
          <ul>
            <li>Est-elle imposée directement ?</li>
            <li>Sont-ce les associés qui paient l'impôt ?</li>
            <li>Peut-on choisir son régime d'imposition ?</li>
          </ul>
        </div>

        <p>Chez <strong>CINEF-EXPERTISE COMPTABLE</strong>, nous vous éclairons sur les règles applicables en nous basant sur le Code Général des Impôts. Comprendre la fiscalité de votre société civile est la première étape pour prendre des décisions éclairées et optimiser votre situation.</p>
      </div>

      <h2 class="section-title">
        <span class="title-icon"></span>
        Le principe fondamental : la "Transparence fiscale"
      </h2>
      
      <div class="highlight-box">
        <p><strong>Par défaut, une société civile est considérée comme fiscalement transparente.</strong></p>
        <p>Qu'est-ce que cela signifie ? C'est simple : la société elle-même n'est pas redevable de l'impôt sur les bénéfices. L'administration fiscale "regarde à travers" la société pour imposer directement chaque associé sur la part des bénéfices qui lui revient, proportionnellement à sa participation au capital.</p>
      </div>

      <p class="info-text">Cependant, selon la nature de son activité, la société civile ne sera pas soumise au même type d'impôt.</p>

      <h2 class="section-title">
        <span class="title-icon"></span>
        Cas 1 : Le régime de droit commun - L'Impôt sur les Bénéfices d'Affaires (IBA)
      </h2>
      
      <p>Ce régime s'applique à la plupart des sociétés civiles qui exercent une activité non-commerciale (agricole, libérale, intellectuelle). C'est le cas typique des Sociétés Civiles Professionnelles (SCP) de médecins, d'avocats, ou d'architectes.</p>

      <div class="process-card">
        <h4>Comment ça marche ?</h4>
        <p class="legal-ref">Conformément à l'article 57 du CGI, les associés sont personnellement soumis à l'impôt sur les bénéfices d'affaires pour la part des bénéfices sociaux correspondant à leurs droits dans la société.</p>
        
        <div class="steps-list">
          <div class="step-item">
            <span class="step-number">1</span>
            <p>La société détermine son bénéfice fiscal selon les règles applicables.</p>
          </div>
          <div class="step-item">
            <span class="step-number">2</span>
            <p>Ce bénéfice est ensuite réparti entre les associés.</p>
          </div>
          <div class="step-item">
            <span class="step-number">3</span>
            <p>Chaque associé est personnellement redevable de l'IBA sur sa quote-part.</p>
          </div>
        </div>

        <p class="note-text">Pour des raisons pratiques, la loi prévoit que la société se charge de déclarer et de payer l'impôt au nom de ses associés, ainsi que les acomptes provisionnels prévus à l'article 51 du CGI.</p>
      </div>

      <div class="example-box iba-example">
        <h5>💡 Exemple concret</h5>
        <p>La SCP "LUCRATIF" a deux associés à 50/50 et réalise un bénéfice de <strong>9 000 000 FCFA</strong>.</p>
        <p>Chaque associé sera imposé sur <strong>4 500 000 FCFA</strong>.</p>
        <p class="example-note">C'est la société qui versera au Trésor public l'impôt dû par chacun.</p>
      </div>

      <h2 class="section-title">
        <span class="title-icon"></span>
        Cas 2 : L'exception immobilière - L'Impôt sur le Revenu Foncier (IRF)
      </h2>
      
      <p>Ce régime concerne spécifiquement les sociétés civiles dites "à prépondérance immobilière", c'est-à-dire celles dont le chiffre d'affaires est composé à plus de 50% de revenus fonciers, comme le stipule l'article 101 du CGI. C'est le cas de la majorité des Sociétés Civiles Immobilières (SCI).</p>

      <div class="alert-box warning">
        <h4>⚠️ Comment ça marche ? La grande différence !</h4>
        <p>L'imposition n'est pas basée sur le bénéfice (recettes - charges), mais sur le <strong>montant brut des loyers perçus</strong>, conformément à l'article 103 du CGI.</p>
        <p class="alert-emphasis">Cela signifie que même si la société a un résultat fiscal négatif (plus de charges que de loyers), les associés seront tout de même imposés sur la base des loyers encaissés.</p>
      </div>

      <div class="example-box irf-example">
        <h5>💡 Exemple concret</h5>
        <p>La SCI "DNH & APPARTS" a encaissé <strong>54 000 000 FCFA</strong> de loyers, mais a un résultat déficitaire de <strong>-9 000 000 FCFA</strong>.</p>
        <p class="example-highlight">L'impôt sera tout de même calculé sur les <strong>54 000 000 FCFA</strong> de revenus bruts et réparti entre les associés.</p>
      </div>

      <h2 class="section-title">
        <span class="title-icon">🎯</span>
        Cas 3 : Le choix stratégique - L'Option pour l'Impôt sur les Sociétés (IS)
      </h2>
      
      <p>La loi offre une flexibilité importante. L'article 3, point 3 du CGI autorise toute société civile, quelle que soit son activité, à choisir d'être soumise à l'Impôt sur les Sociétés (IS).</p>

      <div class="impact-card">
        <h4>Qu'est-ce que cela change ?</h4>
        <p>En optant pour l'IS, la société n'est plus "transparente". Elle devient elle-même le contribuable.</p>
        
        <div class="impact-list">
          <div class="impact-item">
            <span class="impact-icon">✓</span>
            <p>La société calcule son bénéfice et paie l'impôt en son propre nom.</p>
          </div>
          <div class="impact-item">
            <span class="impact-icon">✓</span>
            <p>Elle est soumise aux mêmes règles que les sociétés commerciales, y compris le paiement d'un impôt minimum prévu à l'article 47 du CGI.</p>
          </div>
          <div class="impact-item">
            <span class="impact-icon">✓</span>
            <p>Les associés ne sont imposés personnellement que s'ils décident de se distribuer des dividendes.</p>
          </div>
        </div>
      </div>

      <div class="expert-advice">
        <div class="advice-header">
          <span class="advice-icon">👨‍💼</span>
          <h4>Le conseil de l'expert</h4>
        </div>
        <p>Cette option doit être mûrement réfléchie. Elle peut être très intéressante si vous souhaitez réinvestir les bénéfices dans la société plutôt que de vous les distribuer.</p>
        <p class="warning-text"><strong>⚠️ Attention :</strong> Selon l'article 3 du CGI, cette option est <strong>irrévocable</strong>. La décision doit donc faire l'objet d'une analyse approfondie.</p>
      </div>

      <h2 class="section-title conclusion-title">
        <span class="title-icon"></span>
        Conclusion : Quel régime pour votre société ?
      </h2>
      
      <div class="conclusion-box">
        <p>Le régime fiscal de votre société civile n'est pas anodin. Il a des conséquences directes sur la charge fiscale globale et sur la gestion de votre trésorerie. Le choix dépend de votre activité (libérale ou immobilière) et de votre stratégie patrimoniale (distribution ou capitalisation des revenus).</p>
      </div>

      <div class="services-card">
        <h4>En tant qu'expert-comptable, notre rôle est de vous accompagner pour :</h4>
        <ul class="services-list">
          <li>Choisir la forme juridique et le régime fiscal les plus adaptés à votre projet.</li>
          <li>Assurer la conformité de vos déclarations, que ce soit au nom de la société ou des associés.</li>
          <li>Simuler les différentes options (transparence vs IS) pour optimiser votre fiscalité en toute légalité.</li>
        </ul>
      </div>

      <div class="cta-box">
        <h4>Pour sécuriser la gestion de votre société civile et faire les bons choix fiscaux</h4>
        <p>Contactez l'équipe de <strong>CINEF-EC</strong>. Nous sommes votre partenaire pour une gestion sereine et optimisée.</p>
      </div>

      <div class="resources-section">
        <h4> Pour aller plus loin</h4>
        <ul class="resources-list">
          <li>Lire la note circulaire N°1463 de la DGI relative au régime fiscal des sociétés civiles</li>
          <li><a href="https://www.impots.bj/" target="_blank">Site de la Direction Générale des Impôts du Bénin</a></li>
          <li><a href="https://oeccabenin.bj/" target="_blank">Site de l'Ordre des Experts-comptables</a></li>
        </ul>
      </div>
    `,
    image: "../assets/images/blog7.jpg",
    categories: ["Fiscalité", "Droit des Sociétés", "Conseil"],
    tags: [
      "SociétéCivile",
      "SCI",
      "Fiscalité",
      "IBA",
      "IRF",
      "IS",
      "Bénin",
      "CGI",
      "ExpertComptable",
    ],
    date: "10 octobre 2025",
    author: "Wilfrid DEDJI",
    authorTitle: "Expert Comptable",
    readTime: "10 min",
    featured: true,
  };

  const similarBlogs = [
    {
      id: 1,
      title: "Optimisation fiscale pour les PME au Bénin",
      image: "/assets/images/image1.png",
      date: "5 octobre 2025",
      category: "Fiscalité",
    },
    {
      id: 3,
      title: "Guide complet de la création d'entreprise au Bénin",
      image: "/assets/images/image8.png",
      date: "28 septembre 2025",
      category: "Conseil",
    },
  ];

  return (
    <>
      <Header />
      <div className="blog-content">
        <div className="container">
          <nav className="breadcrumb-nav mb-4">
            <Link to="/" className="breadcrumb-link">Accueil</Link>
            <span className="breadcrumb-separator">/</span>
            <Link to="/actualites/blog" className="breadcrumb-link">Blog</Link>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">Société Civile au Bénin</span>
          </nav>

          <div className="row">
            <div className="col-lg-8">
              <article className="blog-article">
                <div className="article-header">
                  <div className="categories-row">
                    {blogPost.categories.map((category, index) => (
                      <span key={index} className="category-pill">
                        {category}
                      </span>
                    ))}
                  </div>
                  
                  <h1 className="article-title">{blogPost.title}</h1>

                  <div className="article-meta">
                    <div className="meta-group">
                      <FaCalendarAlt className="meta-icon" />
                      <span>{blogPost.date}</span>
                    </div>
                    <div className="meta-group">
                      <FaUser className="meta-icon" />
                      <span>{blogPost.author}</span>
                    </div>
                    <div className="meta-group">
                      <FaClock className="meta-icon" />
                      <span>{blogPost.readTime} de lecture</span>
                    </div>
                  </div>
                </div>

               

                <div className="article-excerpt">
                  <div className="excerpt-icon">
                    <FaFileAlt />
                  </div>
                  <p>{blogPost.excerpt}</p>
                </div>

                <div
                  className="article-body"
                  dangerouslySetInnerHTML={{ __html: blogPost.content }}
                />

                <div className="article-footer">
                  <div className="tags-section">
                    <span className="tags-label">Mots-clés :</span>
                    <div className="tags-wrapper">
                      {blogPost.tags.map((tag, index) => (
                        <Link key={index} to="#" className="tag-item">
                          #{tag}
                        </Link>
                      ))}
                    </div>
                  </div>

                  <div className="share-section">
                    <h5 className="share-heading">
                      <FaShare className="me-2" />
                      Partager cet article
                    </h5>
                    <div className="social-share-buttons">
                      <a href="#" className="share-btn linkedin-btn" title="LinkedIn">
                        <FaLinkedin />
                      </a>
                      <a href="#" className="share-btn twitter-btn" title="Twitter">
                        <FaTwitter />
                      </a>
                      <a href="#" className="share-btn facebook-btn" title="Facebook">
                        <FaFacebook />
                      </a>
                    </div>
                  </div>

                  <div className="navigation-section">
                    <Link to="/actualites/blog" className="back-button">
                      <FaChevronLeft />
                      <span>Retour au blog</span>
                    </Link>
                  </div>
                </div>
              </article>
            </div>

            <aside className="col-lg-4">
              <div className="sidebar-card author-card">
                <div className="card-header-custom">
                  <FaUser className="header-icon" />
                  <h4>À propos de l'auteur</h4>
                </div>
                <div className="author-content">
                  <div className="author-avatar">
                    <img
                      src="../assets/images/image5.png"
                      alt={blogPost.author}
                    />
                  </div>
                  <h5 className="author-name">{blogPost.author}</h5>
                  <p className="author-role">{blogPost.authorTitle}</p>
                  <p className="author-description">
                    Expert-comptable certifié OECCA, spécialisé dans l'accompagnement fiscal et juridique des entreprises béninoises. Plus de 15 ans d'expérience dans le conseil aux PME et sociétés civiles.
                  </p>
                </div>
              </div>

              <div className="sidebar-card similar-articles-card">
                <div className="card-header-custom">
                  <FaFileAlt className="header-icon" />
                  <h4>Articles similaires</h4>
                </div>
                <div className="similar-articles-list">
                  {similarBlogs.map((blog) => (
                    <Link
                      key={blog.id}
                      to={`/blog/${blog.id}`}
                      className="similar-article-item"
                    >
                      <div className="article-thumb">
                        <img src={blog.image} alt={blog.title} />
                        <span className="thumb-category">{blog.category}</span>
                      </div>
                      <div className="article-info">
                        <h6 className="article-title-small">{blog.title}</h6>
                        <div className="article-date-small">
                          <FaCalendarAlt />
                          <span>{blog.date}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="sidebar-card newsletter-card">
                <div className="card-header-custom">
                  <FaCalculator className="header-icon" />
                  <h4>Newsletter Fiscale</h4>
                </div>
                <p className="newsletter-text">
                  Recevez chaque mois nos analyses fiscales, les actualités juridiques et nos conseils d'experts directement dans votre boîte mail.
                </p>
                <form className="newsletter-form">
                  <input
                    type="email"
                    className="newsletter-input"
                    placeholder="Votre email professionnel"
                    required
                  />
                  <button type="submit" className="newsletter-btn">
                    S'abonner
                  </button>
                </form>
              </div>

              <div className="sidebar-card resources-card">
                <div className="card-header-custom">
                  <FaBuilding className="header-icon" />
                  <h4>Ressources Officielles</h4>
                </div>
                <ul className="resources-links">
                  <li>
                    <a href="https://www.impots.bj/" target="_blank" rel="noopener noreferrer">
                      Direction Générale des Impôts
                      <span className="link-arrow">→</span>
                    </a>
                  </li>
                  <li>
                    <a href="https://oeccabenin.bj/" target="_blank" rel="noopener noreferrer">
                      Ordre des Experts-Comptables
                      <span className="link-arrow">→</span>
                    </a>
                  </li>
                  <li>
                    <a href="#" target="_blank" rel="noopener noreferrer">
                      Code Général des Impôts
                      <span className="link-arrow">→</span>
                    </a>
                  </li>
                  <li>
                    <Link to="/services/comptabilite-fiscalite">
                      Nos Services
                      <span className="link-arrow">→</span>
                    </Link>
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </div>

      <style jsx>{`
        @import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Merriweather:wght@300;400;700;900&display=swap");

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .blog-content {
          background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%);
          padding: 4rem 0 5rem;
          font-family: "Inter", sans-serif;
          color: #2c3e50;
          min-height: 100vh;
        }

        /* Breadcrumb */
        .breadcrumb-nav {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem 0;
          font-size: 0.9rem;
        }

        .breadcrumb-link {
          color: #6c757d;
          text-decoration: none;
          transition: color 0.2s;
          font-weight: 500;
        }

        .breadcrumb-link:hover {
          color: #198754;
        }

        .breadcrumb-separator {
          color: #adb5bd;
        }

        .breadcrumb-current {
          color: #2c3e50;
          font-weight: 600;
        }

        /* Article Header */
        .blog-article {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
        }

        .article-header {
          padding: 2.5rem 2.5rem 2rem;
        }

        .categories-row {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }

        .category-pill {
          display: inline-block;
          background: linear-gradient(135deg, #198754, #20c997);
          color: white;
          padding: 0.5rem 1.25rem;
          border-radius: 25px;
          font-size: 0.8rem;
          font-weight: 700;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          box-shadow: 0 2px 8px rgba(25, 135, 84, 0.3);
        }

        .article-title {
          font-family: "Merriweather", serif;
          font-size: 3rem;
          font-weight: 900;
          line-height: 1.15;
          color: #1a1a1a;
          margin-bottom: 1.75rem;
          letter-spacing: -0.5px;
        }

        .article-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 2rem;
          padding-top: 1.5rem;
          border-top: 2px solid #e9ecef;
        }

        .meta-group {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          color: #6c757d;
          font-size: 0.95rem;
          font-weight: 500;
        }

        .meta-icon {
          color: #198754;
          font-size: 1rem;
        }

        /* Featured Image */
        .featured-image-wrapper {
          position: relative;
          width: 100%;
          height: 450px;
          overflow: hidden;
        }

        .featured-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .image-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 50%;
          background: linear-gradient(to top, rgba(0,0,0,0.5), transparent);
        }

        /* Article Excerpt */
        .article-excerpt {
          padding: 2.5rem;
          background: linear-gradient(135deg, rgba(25, 135, 84, 0.05), rgba(220, 53, 69, 0.05));
          border-left: 5px solid #198754;
          margin: 2.5rem;
          border-radius: 0 8px 8px 0;
          display: flex;
          gap: 1.5rem;
          align-items: flex-start;
        }

        .excerpt-icon {
          flex-shrink: 0;
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #198754, #20c997);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.5rem;
        }

        .article-excerpt p {
          font-size: 1.2rem;
          line-height: 1.7;
          color: #2c3e50;
          font-weight: 500;
          margin: 0;
        }

        /* Article Body */
        .article-body {
          padding: 2.5rem;
          font-size: 1.05rem;
          line-height: 1.8;
          color: #495057;
        }

        .intro-section {
          margin-bottom: 3rem;
        }

        .lead-text {
          font-size: 1.15rem;
          line-height: 1.8;
          color: #495057;
          margin-bottom: 2rem;
        }

        .question-box {
          background: #f8f9fa;
          padding: 1.5rem 2rem;
          border-radius: 12px;
          border-left: 4px solid #dc3545;
          margin: 2rem 0;
        }

        .question-box h4 {
          font-size: 1.1rem;
          font-weight: 700;
          color: #2c3e50;
          margin-bottom: 1rem;
        }

        .question-box ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .question-box li {
          padding: 0.5rem 0;
          padding-left: 1.5rem;
          position: relative;
          color: #495057;
        }

        .question-box li::before {
          content: "?";
          position: absolute;
          left: 0;
          color: #dc3545;
          font-weight: 700;
          font-size: 1.2rem;
        }

        .section-title {
          font-family: "Merriweather", serif;
          font-size: 2rem;
          font-weight: 700;
          color: #1a1a1a;
          margin: 3rem 0 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 3px solid transparent;
          border-image: linear-gradient(to right, #dc3545, #198754) 1;
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .title-icon {
          font-size: 2.5rem;
          flex-shrink: 0;
        }

        .highlight-box {
          background: linear-gradient(135deg, #fff3cd, #f8f9fa);
          padding: 2rem;
          border-radius: 12px;
          border-left: 5px solid #ffc107;
          margin: 2rem 0;
        }

        .highlight-box p {
          margin-bottom: 1rem;
        }

        .highlight-box p:last-child {
          margin-bottom: 0;
        }

        .info-text {
          font-style: italic;
          color: #6c757d;
          margin: 1.5rem 0;
        }

        .process-card {
          background: white;
          border: 2px solid #e9ecef;
          border-radius: 12px;
          padding: 2rem;
          margin: 2rem 0;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
        }

        .process-card h4 {
          font-size: 1.3rem;
          font-weight: 700;
          color: #198754;
          margin-bottom: 1.5rem;
        }

        .legal-ref {
          background: #f8f9fa;
          padding: 1rem 1.5rem;
          border-radius: 8px;
          font-size: 0.95rem;
          color: #495057;
          margin-bottom: 1.5rem;
          font-style: italic;
        }

        .steps-list {
          margin: 2rem 0;
        }

        .step-item {
          display: flex;
          gap: 1.5rem;
          align-items: flex-start;
          margin-bottom: 1.5rem;
        }

        .step-number {
          flex-shrink: 0;
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #198754, #20c997);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.1rem;
          box-shadow: 0 4px 12px rgba(25, 135, 84, 0.3);
        }

        .step-item p {
          flex: 1;
          margin: 0;
          padding-top: 0.5rem;
        }

        .note-text {
          background: #e7f5ff;
          padding: 1rem 1.5rem;
          border-radius: 8px;
          border-left: 4px solid #0dcaf0;
          margin-top: 1.5rem;
          font-size: 0.95rem;
        }

        .example-box {
          padding: 2rem;
          border-radius: 12px;
          margin: 2rem 0;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
        }

        .iba-example {
          background: linear-gradient(135deg, #d1f2eb, #f8f9fa);
          border-left: 5px solid #198754;
        }

        .irf-example {
          background: linear-gradient(135deg, #fff3cd, #f8f9fa);
          border-left: 5px solid #ffc107;
        }

        .example-box h5 {
          font-size: 1.2rem;
          font-weight: 700;
          color: #2c3e50;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .example-box p {
          margin-bottom: 0.75rem;
          font-size: 1.05rem;
        }

        .example-box strong {
          color: #198754;
          font-weight: 700;
        }

        .example-note {
          font-style: italic;
          color: #6c757d;
          font-size: 0.95rem;
          margin-top: 1rem;
        }

        .example-highlight {
          background: white;
          padding: 1rem;
          border-radius: 8px;
          font-weight: 600;
          margin-top: 1rem;
        }

        .alert-box {
          padding: 2rem;
          border-radius: 12px;
          margin: 2rem 0;
          border: 2px solid;
        }

        .alert-box.warning {
          background: linear-gradient(135deg, #fff5f5, #fff);
          border-color: #dc3545;
        }

        .alert-box h4 {
          font-size: 1.2rem;
          font-weight: 700;
          color: #dc3545;
          margin-bottom: 1rem;
        }

        .alert-emphasis {
          background: white;
          padding: 1rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          color: #dc3545;
          margin-top: 1rem;
        }

        .impact-card {
          background: linear-gradient(135deg, #f8f9fa, #e9ecef);
          padding: 2rem;
          border-radius: 12px;
          margin: 2rem 0;
          border: 2px solid #dee2e6;
        }

        .impact-card h4 {
          font-size: 1.3rem;
          font-weight: 700;
          color: #2c3e50;
          margin-bottom: 1.5rem;
        }

        .impact-list {
          margin-top: 1.5rem;
        }

        .impact-item {
          display: flex;
          gap: 1rem;
          align-items: flex-start;
          margin-bottom: 1.25rem;
          background: white;
          padding: 1rem;
          border-radius: 8px;
        }

        .impact-icon {
          flex-shrink: 0;
          width: 30px;
          height: 30px;
          background: #198754;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
        }

        .impact-item p {
          flex: 1;
          margin: 0;
        }

        .expert-advice {
          background: linear-gradient(135deg, #e7f5ff, #f8f9fa);
          padding: 2rem;
          border-radius: 12px;
          border-left: 5px solid #0dcaf0;
          margin: 2rem 0;
        }

        .advice-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .advice-icon {
          font-size: 2rem;
        }

        .advice-header h4 {
          font-size: 1.3rem;
          font-weight: 700;
          color: #2c3e50;
          margin: 0;
        }

        .warning-text {
          background: #fff3cd;
          padding: 1rem 1.5rem;
          border-radius: 8px;
          border-left: 4px solid #ffc107;
          margin-top: 1rem;
        }

        .conclusion-title {
          margin-top: 4rem;
        }

        .conclusion-box {
          background: linear-gradient(135deg, #d1f2eb, #e7f5ff);
          padding: 2rem;
          border-radius: 12px;
          border: 2px solid #20c997;
          margin: 2rem 0;
        }

        .conclusion-box p {
          font-size: 1.1rem;
          line-height: 1.8;
          color: #2c3e50;
          margin: 0;
        }

        .services-card {
          background: white;
          padding: 2rem;
          border-radius: 12px;
          border: 2px solid #198754;
          margin: 2rem 0;
          box-shadow: 0 4px 16px rgba(25, 135, 84, 0.1);
        }

        .services-card h4 {
          font-size: 1.2rem;
          font-weight: 700;
          color: #198754;
          margin-bottom: 1.5rem;
        }

        .services-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .services-list li {
          padding: 1rem;
          margin-bottom: 0.75rem;
          background: #f8f9fa;
          border-radius: 8px;
          border-left: 4px solid #198754;
          transition: all 0.3s ease;
        }

        .services-list li:hover {
          background: #e7f5ff;
          transform: translateX(5px);
        }

        .cta-box {
          background: linear-gradient(135deg, #198754, #20c997);
          color: white;
          padding: 2.5rem;
          border-radius: 12px;
          text-align: center;
          margin: 3rem 0 2rem;
          box-shadow: 0 8px 24px rgba(25, 135, 84, 0.3);
        }

        .cta-box h4 {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
          color: white;
        }

        .cta-box p {
          font-size: 1.1rem;
          margin: 0;
        }

        .resources-section {
          background: #f8f9fa;
          padding: 2rem;
          border-radius: 12px;
          margin: 2rem 0;
        }

        .resources-section h4 {
          font-size: 1.2rem;
          font-weight: 700;
          color: #2c3e50;
          margin-bottom: 1.5rem;
        }

        .resources-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .resources-list li {
          padding: 0.75rem 0;
          border-bottom: 1px solid #dee2e6;
        }

        .resources-list li:last-child {
          border-bottom: none;
        }

        .resources-list a {
          color: #198754;
          text-decoration: none;
          font-weight: 500;
          transition: color 0.2s;
        }

        .resources-list a:hover {
          color: #20c997;
        }

        /* Article Footer */
        .article-footer {
          padding: 2.5rem;
          border-top: 2px solid #e9ecef;
        }

        .tags-section {
          margin-bottom: 2.5rem;
        }

        .tags-label {
          font-weight: 700;
          color: #495057;
          margin-bottom: 1rem;
          display: block;
        }

        .tags-wrapper {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .tag-item {
          display: inline-block;
          background: #f8f9fa;
          color: #495057;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          text-decoration: none;
          font-size: 0.9rem;
          font-weight: 600;
          border: 2px solid #e9ecef;
          transition: all 0.3s ease;
        }

        .tag-item:hover {
          background: #198754;
          color: white;
          border-color: #198754;
          transform: translateY(-2px);
        }

        .share-section {
          padding: 2rem 0;
          border-top: 2px solid #e9ecef;
          border-bottom: 2px solid #e9ecef;
          margin-bottom: 2rem;
        }

        .share-heading {
          font-size: 1.1rem;
          font-weight: 700;
          color: #2c3e50;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
        }

        .social-share-buttons {
          display: flex;
          gap: 1rem;
        }

        .share-btn {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          text-decoration: none;
          font-size: 1.3rem;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .share-btn:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
        }

        .linkedin-btn {
          background: linear-gradient(135deg, #0077b5, #005582);
        }

        .twitter-btn {
          background: linear-gradient(135deg, #1da1f2, #0d8bd9);
        }

        .facebook-btn {
          background: linear-gradient(135deg, #4267b2, #2d4373);
        }

        .navigation-section {
          text-align: center;
        }

        .back-button {
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          background: white;
          color: #495057;
          padding: 1rem 2rem;
          border-radius: 30px;
          text-decoration: none;
          border: 2px solid #e9ecef;
          font-weight: 600;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .back-button:hover {
          background: linear-gradient(135deg, #198754, #20c997);
          color: white;
          border-color: #198754;
          transform: translateX(-5px);
          box-shadow: 0 4px 16px rgba(25, 135, 84, 0.3);
        }

        /* Sidebar */
        .sidebar-card {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
          border: 1px solid #e9ecef;
        }

        .card-header-custom {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #f8f9fa;
        }

        .header-icon {
          color: #198754;
          font-size: 1.5rem;
        }

        .card-header-custom h4 {
          font-size: 1.3rem;
          font-weight: 700;
          color: #2c3e50;
          margin: 0;
        }

        .author-content {
          text-align: center;
        }

        .author-avatar {
          width: 100px;
          height: 100px;
          border-radius: 50%;
          overflow: hidden;
          margin: 0 auto 1.5rem;
          border: 4px solid #f8f9fa;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .author-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .author-name {
          font-size: 1.2rem;
          font-weight: 700;
          color: #2c3e50;
          margin-bottom: 0.5rem;
        }

        .author-role {
          font-size: 0.95rem;
          color: #198754;
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .author-description {
          font-size: 0.95rem;
          color: #6c757d;
          line-height: 1.6;
        }

        .similar-articles-list {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .similar-article-item {
          display: flex;
          gap: 1rem;
          text-decoration: none;
          color: inherit;
          background: #f8f9fa;
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }

        .similar-article-item:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
          border-color: #198754;
        }

        .article-thumb {
          width: 120px;
          height: 90px;
          flex-shrink: 0;
          position: relative;
        }

        .article-thumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .thumb-category {
          position: absolute;
          top: 0.5rem;
          left: 0.5rem;
          background: linear-gradient(135deg, #198754, #20c997);
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 15px;
          font-size: 0.7rem;
          font-weight: 700;
        }

        .article-info {
          padding: 0.75rem 1rem;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .article-title-small {
          font-size: 1rem;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 0.5rem;
          line-height: 1.4;
        }

        .article-date-small {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #6c757d;
          font-size: 0.85rem;
        }

        .article-date-small svg {
          font-size: 0.75rem;
        }

        .newsletter-text {
          font-size: 0.95rem;
          color: #6c757d;
          line-height: 1.6;
          margin-bottom: 1.5rem;
        }

        .newsletter-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .newsletter-input {
          width: 100%;
          padding: 0.9rem 1.5rem;
          border: 2px solid #e9ecef;
          border-radius: 30px;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          outline: none;
        }

        .newsletter-input:focus {
          border-color: #198754;
          box-shadow: 0 0 0 4px rgba(25, 135, 84, 0.1);
        }

        .newsletter-btn {
          width: 100%;
          padding: 0.9rem 1.5rem;
          background: linear-gradient(135deg, #198754, #20c997);
          color: white;
          border: none;
          border-radius: 30px;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(25, 135, 84, 0.3);
        }

        .newsletter-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(25, 135, 84, 0.4);
        }

        .resources-links {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .resources-links li {
          margin-bottom: 1rem;
        }

        .resources-links a {
          display: flex;
          justify-content: space-between;
          align-items: center;
          text-decoration: none;
          color: #2c3e50;
          font-size: 0.95rem;
          font-weight: 600;
          padding: 0.75rem;
          background: #f8f9fa;
          border-radius: 8px;
          transition: all 0.3s ease;
        }

        .resources-links a:hover {
          background: #198754;
          color: white;
          transform: translateX(5px);
        }

        .link-arrow {
          font-size: 1.2rem;
          font-weight: 700;
          transition: transform 0.3s ease;
        }

        .resources-links a:hover .link-arrow {
          transform: translateX(5px);
        }

        /* Responsive */
        @media (max-width: 992px) {
          .article-title {
            font-size: 2.5rem;
          }

          .section-title {
            font-size: 1.7rem;
          }
        }

        @media (max-width: 768px) {
          .blog-content {
            padding: 2rem 0 3rem;
          }

          .article-title {
            font-size: 2rem;
          }

          .article-header,
          .article-body,
          .article-footer {
            padding: 1.5rem;
          }

          .article-excerpt {
            margin: 1.5rem;
            padding: 1.5rem;
            flex-direction: column;
            align-items: center;
            text-align: center;
          }

          .featured-image-wrapper {
            height: 300px;
          }

          .article-meta {
            flex-direction: column;
            gap: 1rem;
          }

          .section-title {
            font-size: 1.5rem;
            flex-direction: column;
            text-align: center;
          }

          .similar-article-item {
            flex-direction: column;
          }

          .article-thumb {
            width: 100%;
            height: 160px;
          }

          .article-info {
            padding: 1rem;
          }
        }

        @media (max-width: 576px) {
          .article-title {
            font-size: 1.75rem;
          }

          .categories-row {
            justify-content: center;
          }

          .social-share-buttons {
            justify-content: center;
          }
        }
      `}</style>
      <Footer />
    </>
  );
};

export default DetailBlog7;